import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { UserCog, Shield, Trash2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import Loader from '../../components/Loader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const roles = [
  { value: 'superadmin', label: 'Super Admin', color: 'text-yellow-400' },
  { value: 'admin', label: 'Admin', color: 'text-red-400' },
  { value: 'moderator', label: 'Modérateur', color: 'text-blue-400' },
  { value: 'pending', label: 'En attente', color: 'text-gray-400' }
];

const fetchAdminsRequest = async (token) => axios.get(`${API}/admin/admins`, {
  headers: { Authorization: `Bearer ${token}` }
});

const AdminUsers = () => {
  const { token, admin: currentAdmin } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);

  const refreshAdmins = async () => {
    try {
      const response = await fetchAdminsRequest(token);
      setAdmins(response.data);
    } catch (error) {
      console.error('Failed to fetch admins:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadAdmins = async () => {
      try {
        const response = await fetchAdminsRequest(token);
        setAdmins(response.data);
      } catch (error) {
        console.error('Failed to fetch admins:', error);
        toast.error('Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };

    loadAdmins();
  }, [token]);

  const handleRoleChange = async (adminId, newRole) => {
    try {
      await axios.put(`${API}/admin/admins/${adminId}/role?role=${newRole}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Rôle mis à jour !');
      refreshAdmins();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
      console.error(error);
    }
  };

  const handleDelete = async (adminId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet administrateur ?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/admins/${adminId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Administrateur supprimé !');
      refreshAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
      console.error(error);
    }
  };

  const getRoleInfo = (role) => roles.find(r => r.value === role) || roles[3];

  if (loading) {
    return <Loader text="Chargement des administrateurs..." />;
  }

  const pendingAdmins = admins.filter(a => a.role === 'pending');
  const activeAdmins = admins.filter(a => a.role !== 'pending');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-2xl text-white uppercase tracking-wider flex items-center gap-2">
          <UserCog className="w-6 h-6 text-violet-400" />
          Gestion des Administrateurs
        </h1>
        <p className="text-gray-400">
          {activeAdmins.length} actif(s) • {pendingAdmins.length} en attente
        </p>
      </div>

      {/* Pending Admins */}
      {pendingAdmins.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-xl p-6 border border-yellow-500/30"
        >
          <h2 className="font-heading font-bold text-lg text-yellow-400 uppercase tracking-wider mb-4">
            En attente d'approbation
          </h2>
          
          <div className="space-y-3">
            {pendingAdmins.map((admin) => (
              <div
                key={admin.id}
                className="flex items-center gap-4 p-4 bg-black/30 rounded-lg"
                data-testid={`pending-admin-${admin.id}`}
              >
                {admin.discord_avatar ? (
                  <img 
                    src={admin.discord_avatar}
                    alt={admin.discord_username}
                    className="w-10 h-10 rounded-full"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
                    <span className="text-gray-400 font-bold">
                      {admin.discord_username?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                
                <div className="flex-1">
                  <p className="text-white font-medium">{admin.discord_username}</p>
                  <p className="text-gray-500 text-sm">
                    Inscrit le {new Date(admin.created_at).toLocaleDateString('fr-FR')}
                  </p>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleRoleChange(admin.id, 'moderator')}
                    className="bg-green-600 hover:bg-green-500"
                    data-testid={`approve-admin-${admin.id}`}
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Approuver
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDelete(admin.id)}
                    data-testid={`reject-admin-${admin.id}`}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Refuser
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Active Admins */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="glass rounded-xl p-6"
      >
        <h2 className="font-heading font-bold text-lg text-white uppercase tracking-wider mb-4">
          Administrateurs actifs
        </h2>
        
        {activeAdmins.length === 0 ? (
          <p className="text-gray-400 text-center py-8">Aucun administrateur actif</p>
        ) : (
          <div className="space-y-3">
            {activeAdmins.map((admin) => {
              const roleInfo = getRoleInfo(admin.role);
              const isCurrentUser = admin.id === currentAdmin?.id;
              
              return (
                <div
                  key={admin.id}
                  className={`flex items-center gap-4 p-4 bg-black/30 rounded-lg ${
                    isCurrentUser ? 'border border-violet-500/30' : ''
                  }`}
                  data-testid={`admin-${admin.id}`}
                >
                  {admin.discord_avatar ? (
                    <img 
                      src={admin.discord_avatar}
                      alt={admin.discord_username}
                      className="w-10 h-10 rounded-full"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-violet-600/20 flex items-center justify-center">
                      <span className="text-violet-400 font-bold">
                        {admin.discord_username?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium">{admin.discord_username}</p>
                      {isCurrentUser && (
                        <span className="text-xs text-violet-400">(vous)</span>
                      )}
                    </div>
                    <p className={`text-sm ${roleInfo.color}`}>{roleInfo.label}</p>
                  </div>
                  
                  {!isCurrentUser && (
                    <div className="flex items-center gap-3">
                      <Select 
                        value={admin.role} 
                        onValueChange={(value) => handleRoleChange(admin.id, value)}
                      >
                        <SelectTrigger className="w-40 bg-black/50 border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0a0a] border-white/10">
                          {roles.filter(r => r.value !== 'pending').map((role) => (
                            <SelectItem key={role.value} value={role.value} className="text-white">
                              {role.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      <button
                        onClick={() => handleDelete(admin.id)}
                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        data-testid={`delete-admin-${admin.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </motion.div>

      {/* Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="glass rounded-xl p-6 border border-blue-500/20 bg-blue-500/5"
      >
        <div className="flex items-start gap-4">
          <Shield className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-heading font-bold text-blue-400 uppercase tracking-wider mb-2">
              Niveaux d'accès
            </h3>
            <ul className="text-gray-300 text-sm space-y-1">
              <li><span className="text-yellow-400">Super Admin</span> - Accès complet, gestion des autres admins</li>
              <li><span className="text-red-400">Admin</span> - Gestion du contenu (staff, règles, incidents, paramètres)</li>
              <li><span className="text-blue-400">Modérateur</span> - Gestion limitée (staff, incidents)</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminUsers;
